// Renders a sample competitor-analysis report into a standalone, self-contained
// HTML document in the editorial style Soku actually generates: masthead, stat
// band, a flexible stack of sections (tables with highlight rows + colored
// pills, card grids, horizontal-bar / time-series / scatter / radar charts,
// prose), a strategic-implications conclusion, an optional data-source section,
// and an optional confidentiality notice — plus a sticky in-report nav, hover
// tooltips, sortable tables, and reveal-on-scroll.
//
// It is dropped into an <iframe srcDoc> on /free-competitor-analysis so it
// renders in full isolation (own fonts, global CSS, progressive-enhancement
// script). Every block is driven by the typed model below (see
// ./sample-report-data) so a new sample is "swap the numbers and brand names".
//
// All input is static, in-repo "dead data" (no user input); text is still
// HTML-escaped so stray characters render literally.

// ── Data model ────────────────────────────────────────────────────────────────

export type ReportStat = { label: string; value: string; note: string }

export type PillVariant = 'default' | 'accent' | 'up' | 'down' | 'neutral'
/** A table cell: a plain string (rendered with inline markup) or a pill chip. */
export type Cell = string | { text: string; pill: PillVariant }

export type LedeBlock = { type: 'lede'; text: string }
export type ProseBlock = {
  type: 'prose'
  paragraphs: { heading?: string; body: string }[]
}
export type CardsBlock = {
  type: 'cards'
  cols?: 2 | 3
  cards: { title: string; body: string }[]
}
export type TableBlock = {
  type: 'table'
  caption?: string
  columns: string[]
  rows: { cells: Cell[]; highlight?: boolean }[]
  /** Sortable column carets (default true). */
  sortable?: boolean
}
export type BarsBlock = {
  type: 'bars'
  title?: string
  sub?: string
  bars: { label: string; value: number; display?: string; accent?: boolean }[]
}
export type TrendBlock = {
  type: 'trend'
  title?: string
  sub?: string
  yMax?: number
  points: {
    label: string
    value: number
    display?: string
    tip?: string
    accent?: boolean
  }[]
}

export type ScatterPoint = {
  name: string
  x: number // 0–10, left→right
  y: number // 0–10, bottom→top
  size: number
  tip: string
  accent?: boolean
  labelDy?: number
}
export type ScatterBlock = {
  type: 'scatter'
  title: string
  sub: string
  xLabel: string
  yLabel: string
  points: ScatterPoint[]
}

export type RadarSeries = { name: string; values: number[]; accent?: boolean }
export type RadarBlock = {
  type: 'radar'
  title: string
  sub: string
  axes: string[]
  series: RadarSeries[]
}

export type Block =
  | LedeBlock
  | ProseBlock
  | CardsBlock
  | TableBlock
  | BarsBlock
  | TrendBlock
  | ScatterBlock
  | RadarBlock

export type Section = {
  id: string
  /** Short label for the sticky nav; omit to keep the section out of the nav. */
  nav?: string
  title: string
  blocks: Block[]
}

export type CompetitorReport = {
  title: string
  subtitle: string
  navBrand: string
  meta: string
  stats: ReportStat[]
  sections: Section[]
  conclusion: { title: string; paragraphs: { heading?: string; body: string }[] }
  /** Optional closing confidentiality notice (B2B/private reports). */
  confidential?: string
}

// ── Entry point ────────────────────────────────────────────────────────────────

export function buildReportHtml(report: CompetitorReport): string {
  const nav = report.sections
    .filter(s => s.nav)
    .map(s => `<a href="#${s.id}">${escapeHtml(s.nav as string)}</a>`)
    .join('\n      ')

  const sectionsHtml = report.sections
    .map((section, i) => renderSection(section, i + 1))
    .join('\n\n  ')

  const conclusion = `<div class="conclusion reveal">
    <h2>${escapeHtml(report.conclusion.title)}</h2>
    ${report.conclusion.paragraphs.map(renderConclusionPara).join('\n    ')}
  </div>`

  const confidential = report.confidential
    ? `<div class="confidential">${report.confidential
        .split('\n')
        .map(p => `<p>${escapeHtml(p)}</p>`)
        .join('')}</div>`
    : ''

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>${escapeHtml(report.title)}</title>
<style>${STYLES}</style>
</head>
<body>
<nav class="topnav">
  <div class="topnav-inner">
    <a class="nav-brand" href="#top">${escapeHtml(report.navBrand)}</a>
    ${nav}
  </div>
</nav>

<main class="page">
  <header class="masthead" id="top">
    <h1>${escapeHtml(report.title)}</h1>
    <p class="subtitle">${escapeHtml(report.subtitle)}</p>
    <div class="rule"></div>
    <p class="meta">${report.meta}</p>
  </header>

  <div class="stats">
    ${report.stats.map(renderStat).join('\n    ')}
  </div>

  ${sectionsHtml}

  ${conclusion}

  ${confidential}
</main>

<script>${SCRIPT}</script>
</body>
</html>`
}

// ── Section + block renderers ──────────────────────────────────────────────────

function renderSection(section: Section, index: number): string {
  return `<section id="${section.id}" class="reveal">
    <div class="section-head"><div class="badge">${index}</div><h2>${escapeHtml(
      section.title,
    )}</h2></div>
    ${section.blocks.map(renderBlock).join('\n    ')}
  </section>`
}

function renderBlock(block: Block): string {
  switch (block.type) {
    case 'lede':
      return `<p class="lede">${inline(block.text)}</p>`
    case 'prose':
      return `<div class="prose">${block.paragraphs.map(renderConclusionPara).join('\n      ')}</div>`
    case 'cards': {
      const grid = block.cols === 3 ? 'grid-3' : 'grid-2'
      return `<div class="${grid}">
      ${block.cards.map(renderCard).join('\n      ')}
    </div>`
    }
    case 'table':
      return renderTable(block)
    case 'bars':
      return renderBars(block)
    case 'trend':
      return renderTrend(block)
    case 'scatter':
      return `<div class="charts-row">${renderScatter(block)}</div>`
    case 'radar':
      return `<div class="charts-row">${renderRadar(block)}</div>`
  }
}

function renderConclusionPara(p: { heading?: string; body: string }): string {
  const heading = p.heading ? `<strong>${escapeHtml(p.heading)}</strong> ` : ''
  return `<p>${heading}${inline(p.body)}</p>`
}

function renderStat(stat: ReportStat): string {
  return `<div class="stat"><div class="label">${escapeHtml(stat.label)}</div><div class="value">${escapeHtml(
    stat.value,
  )}</div><div class="note">${escapeHtml(stat.note)}</div></div>`
}

function renderCard(card: { title: string; body: string }): string {
  return `<div class="card">
        <h3><span class="dot"></span>${escapeHtml(card.title)}</h3>
        <p>${inline(card.body)}</p>
      </div>`
}

function pillClass(pill: PillVariant): string {
  if (pill === 'accent') return 'pill pill-accent'
  if (pill === 'default') return 'pill'
  return `pill ${pill}`
}

function renderCell(cell: Cell): string {
  if (typeof cell === 'string') return `<td>${inline(cell)}</td>`
  return `<td><span class="${pillClass(cell.pill)}">${escapeHtml(cell.text)}</span></td>`
}

function renderTable(table: TableBlock): string {
  const caption = table.caption
    ? `<div class="chart-title" style="margin-bottom:14px;">${escapeHtml(table.caption)}</div>`
    : ''
  const sortable = table.sortable === false ? '' : ' data-sortable'
  const head = table.columns.map(c => `<th>${escapeHtml(c)}</th>`).join('')
  const body = table.rows
    .map(
      row =>
        `<tr${row.highlight ? ' class="highlight"' : ''}>${row.cells
          .map(renderCell)
          .join('')}</tr>`,
    )
    .join('\n          ')
  return `${caption}<div class="table-wrap">
      <table${sortable}>
        <thead><tr>${head}</tr></thead>
        <tbody>
          ${body}
        </tbody>
      </table>
    </div>`
}

// ── Horizontal bar chart ───────────────────────────────────────────────────────

function renderBars(block: BarsBlock): string {
  const max = Math.max(1, ...block.bars.map(b => b.value))
  const rowH = 28
  const barH = 18
  const top = 14
  const x0 = 64
  const maxW = 560 // leaves room for the value label up to ~720
  const height = top + block.bars.length * rowH

  const rows = block.bars
    .map((b, i) => {
      const y = top + i * rowH
      const w = Math.max(b.value > 0 ? 3 : 0, round((b.value / max) * maxW))
      const fill = b.accent ? 'var(--accent)' : 'var(--chart-ink)'
      const op = b.accent ? '0.85' : '0.78'
      const display = b.display ?? String(b.value)
      return `<text x="56" y="${y + 13}" text-anchor="end" font-size="11" fill="var(--text-2)">${escapeHtml(
        b.label,
      )}</text>
        <rect x="${x0}" y="${y}" width="${w}" height="${barH}" rx="4" fill="${fill}" fill-opacity="${op}"/>
        <text x="${x0 + w + 6}" y="${y + 13}" font-size="10" fill="var(--text-muted)">${escapeHtml(
          display,
        )}</text>`
    })
    .join('\n        ')

  return `<div class="chart-block">
      ${block.title ? `<div class="chart-title">${escapeHtml(block.title)}</div>` : ''}
      ${block.sub ? `<div class="chart-sub">${escapeHtml(block.sub)}</div>` : ''}
      <svg viewBox="0 0 760 ${height}" role="img" aria-label="${escapeAttr(
        block.title ?? 'Bar chart',
      )}">
        ${rows}
      </svg>
    </div>`
}

// ── Time-series (vertical bar) chart ────────────────────────────────────────────

function renderTrend(block: TrendBlock): string {
  const max = Math.max(1, block.yMax ?? Math.max(...block.points.map(p => p.value)))
  const plotTop = 30
  const plotBottom = 230
  const plotH = plotBottom - plotTop
  const x0 = 60
  const x1 = 780
  const n = block.points.length
  const slot = (x1 - x0) / n
  const barW = Math.min(48, slot * 0.62)

  const gridY = [0, 0.25, 0.5, 0.75, 1]
  const grid = gridY
    .map(g => {
      const y = round(plotBottom - g * plotH)
      return `<line x1="${x0}" y1="${y}" x2="${x1}" y2="${y}"/>`
    })
    .join('\n          ')
  const yLabels = gridY
    .map(g => {
      const y = round(plotBottom - g * plotH)
      return `<text x="52" y="${y + 4}">${formatCompact(round(g * max))}</text>`
    })
    .join('\n          ')

  const bars = block.points
    .map((p, i) => {
      const cx = x0 + slot * i + slot / 2
      const x = round(cx - barW / 2)
      const h = Math.max(p.value > 0 ? 1 : 0, round((p.value / max) * plotH))
      const y = round(plotBottom - h)
      const op = p.accent ? '0.85' : '0.55'
      const fill = p.accent ? 'var(--accent)' : 'var(--chart-ink)'
      const tip = p.tip ? ` data-tip="${escapeAttr(p.tip)}"` : ''
      return `<rect x="${x}" y="${y}" width="${round(barW)}" height="${h}" rx="3" fill="${fill}" opacity="${op}"${tip}/>`
    })
    .join('\n        ')

  const xLabels = block.points
    .map((p, i) => {
      const cx = round(x0 + slot * i + slot / 2)
      return `<text x="${cx}" y="246">${escapeHtml(p.label)}</text>`
    })
    .join('\n          ')

  return `<div class="chart-block">
      ${block.title ? `<div class="chart-title">${escapeHtml(block.title)}</div>` : ''}
      ${block.sub ? `<div class="chart-sub">${escapeHtml(block.sub)}</div>` : ''}
      <svg viewBox="0 0 820 260" role="img" aria-label="${escapeAttr(block.title ?? 'Trend chart')}">
        <g stroke="var(--chart-grid)" stroke-width="1">
          ${grid}
        </g>
        <line x1="${x0}" y1="${plotBottom}" x2="${x1}" y2="${plotBottom}" stroke="var(--border-strong)" stroke-width="1.5"/>
        <g font-family="var(--font-body)" font-size="10" fill="var(--text-muted)" text-anchor="end">
          ${yLabels}
        </g>
        ${bars}
        <g font-family="var(--font-body)" font-size="9" fill="var(--text-2)" text-anchor="middle">
          ${xLabels}
        </g>
      </svg>
    </div>`
}

// ── Scatter plot ────────────────────────────────────────────────────────────────

const SC = { x0: 80, x1: 720, y0: 40, y1: 290 }
function scatterX(x: number): number {
  return SC.x0 + (clamp01(x) / 10) * (SC.x1 - SC.x0)
}
function scatterY(y: number): number {
  return SC.y1 - (clamp01(y) / 10) * (SC.y1 - SC.y0)
}

function renderScatter(scatter: ScatterBlock): string {
  const bubbles = scatter.points
    .filter(p => !p.accent)
    .map(p => {
      const cx = round(scatterX(p.x))
      const cy = round(scatterY(p.y))
      return `<circle cx="${cx}" cy="${cy}" r="${p.size}" data-tip="${escapeAttr(p.tip)}"/>`
    })
    .join('\n            ')

  const accent = scatter.points.find(p => p.accent)
  const accentCircle = accent
    ? `<circle cx="${round(scatterX(accent.x))}" cy="${round(
        scatterY(accent.y),
      )}" r="${accent.size}" fill="var(--accent)" fill-opacity="0.92" data-tip="${escapeAttr(
        accent.tip,
      )}"/>`
    : ''

  const labels = scatter.points
    .map(p => {
      const cx = round(scatterX(p.x))
      const cy = round(scatterY(p.y))
      const dy = p.labelDy ?? p.size + 14
      const fill = p.accent
        ? ' fill="var(--accent)" font-weight="500"'
        : ' font-size="10"'
      return `<text x="${cx}" y="${cy + dy}" text-anchor="middle"${fill}>${escapeHtml(
        p.name,
      )}</text>`
    })
    .join('\n            ')

  return `<div class="chart-block">
        <div class="chart-title">${escapeHtml(scatter.title)}</div>
        <div class="chart-sub">${escapeHtml(scatter.sub)}</div>
        <svg viewBox="0 0 760 340" role="img" aria-label="${escapeAttr(scatter.title)}">
          <g stroke="var(--chart-grid)" stroke-width="1">
            <line x1="250" y1="40" x2="250" y2="290"/>
            <line x1="420" y1="40" x2="420" y2="290"/>
            <line x1="590" y1="40" x2="590" y2="290"/>
            <line x1="80" y1="130" x2="720" y2="130"/>
            <line x1="80" y1="210" x2="720" y2="210"/>
          </g>
          <line x1="80" y1="290" x2="720" y2="290" stroke="var(--border-strong)" stroke-width="1.5"/>
          <line x1="80" y1="40" x2="80" y2="290" stroke="var(--border-strong)" stroke-width="1.5"/>
          <g fill="var(--chart-ink)" fill-opacity="0.78">
            ${bubbles}
          </g>
          ${accentCircle}
          <g font-family="var(--font-body)" font-size="11" fill="var(--text-2)">
            ${labels}
          </g>
          <text x="400" y="320" text-anchor="middle" font-size="11" fill="var(--text-muted)">${escapeHtml(
            scatter.xLabel,
          )} →</text>
          <text x="62" y="165" text-anchor="middle" font-size="11" fill="var(--text-muted)" transform="rotate(-90 62 165)">${escapeHtml(
            scatter.yLabel,
          )} →</text>
        </svg>
      </div>`
}

// ── Capability radar ─────────────────────────────────────────────────────────────

const RAD = { cx: 190, cy: 155, r: 115 }
function radarPoint(axisIndex: number, value: number): [number, number] {
  const angle = (-90 + axisIndex * 60) * (Math.PI / 180)
  const r = (clamp01(value) / 10) * RAD.r
  return [RAD.cx + r * Math.cos(angle), RAD.cy + r * Math.sin(angle)]
}
function radarPolygon(values: number[]): string {
  return values.map((v, i) => radarPoint(i, v).map(round).join(',')).join(' ')
}

function renderRadar(radar: RadarBlock): string {
  const rings = [10, 7.5, 5, 2.5]
    .map(level => {
      const pts = radar.axes
        .map((_, i) => radarPoint(i, level).map(round).join(','))
        .join(' ')
      return `<polygon points="${pts}"/>`
    })
    .join('\n            ')

  const spokes = radar.axes
    .map((_, i) => {
      const [x, y] = radarPoint(i, 10).map(round)
      return `<line x1="${RAD.cx}" y1="${RAD.cy}" x2="${x}" y2="${y}"/>`
    })
    .join('\n            ')

  const accent = radar.series.find(s => s.accent) ?? radar.series[0]
  const other = radar.series.find(s => s !== accent)

  const otherPoly = other
    ? `<polygon id="radar-other" points="${radarPolygon(other.values)}"
                   fill="var(--chart-ink)" fill-opacity="0.06"
                   stroke="var(--chart-ink)" stroke-width="1.5" stroke-dasharray="4 3"
                   data-tip="${escapeAttr(`<b>${other.name}</b>`)}"/>`
    : ''
  const accentPoly = `<polygon id="radar-accent" points="${radarPolygon(
    accent.values,
  )}" fill="var(--accent)" fill-opacity="0.13" stroke="var(--accent)" stroke-width="2"/>`

  const accentDots = accent.values
    .map((v, i) => {
      const [x, y] = radarPoint(i, v).map(round)
      return `<circle cx="${x}" cy="${y}" r="4" data-tip="${escapeAttr(
        `<b>${radar.axes[i]}</b><br>${accent.name}: ${v} / 10`,
      )}"/>`
    })
    .join('\n            ')

  const axisLabels = radar.axes
    .map((axis, i) => {
      const [x, y] = radarPoint(i, 11.2)
      const { anchor, dx, dy } = labelPlacement(i)
      return `<text x="${round(x + dx)}" y="${round(y + dy)}" text-anchor="${anchor}">${escapeHtml(
        axis,
      )}</text>`
    })
    .join('\n            ')

  const legend = radar.series
    .map(s => {
      const id = s === accent ? 'radar-accent' : 'radar-other'
      const color = s === accent ? 'var(--accent)' : 'var(--chart-ink)'
      return `<span data-series="${id}"><i style="background:${color}"></i>${escapeHtml(
        s.name,
      )}</span>`
    })
    .join('\n          ')

  return `<div class="chart-block chart-block--narrow">
        <div class="chart-title">${escapeHtml(radar.title)}</div>
        <div class="chart-sub">${escapeHtml(radar.sub)}</div>
        <svg viewBox="0 0 380 340" role="img" aria-label="${escapeAttr(radar.title)}">
          <g fill="none" stroke="var(--chart-grid)" stroke-width="1">
            ${rings}
          </g>
          <g stroke="var(--border)" stroke-width="1">
            ${spokes}
          </g>
          ${otherPoly}
          ${accentPoly}
          <g fill="var(--accent)">
            ${accentDots}
          </g>
          <g font-family="var(--font-body)" font-size="10.5" fill="var(--text-2)">
            ${axisLabels}
          </g>
        </svg>
        <div class="legend">
          ${legend}
        </div>
      </div>`
}

function labelPlacement(i: number): {
  anchor: 'start' | 'middle' | 'end'
  dx: number
  dy: number
} {
  switch (i) {
    case 0:
      return { anchor: 'middle', dx: 0, dy: -2 }
    case 1:
      return { anchor: 'start', dx: 6, dy: 0 }
    case 2:
      return { anchor: 'start', dx: 6, dy: 4 }
    case 3:
      return { anchor: 'middle', dx: 0, dy: 14 }
    case 4:
      return { anchor: 'end', dx: -6, dy: 4 }
    default:
      return { anchor: 'end', dx: -6, dy: 0 }
  }
}

// ── Inline + helpers ─────────────────────────────────────────────────────────

function inline(value: string): string {
  return escapeHtml(value)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
}
function clamp01(n: number): number {
  return Math.max(0, Math.min(10, n))
}
function round(n: number): number {
  return Math.round(n * 10) / 10
}
function formatCompact(n: number): string {
  if (n >= 1000) return `${round(n / 1000)}K`
  return String(Math.round(n))
}
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
// data-tip attributes intentionally carry small HTML (<b>, <br>); only quote +
// bare ampersand need escaping to keep the attribute well-formed.
function escapeAttr(value: string): string {
  return value.replace(/&(?!#?\w+;)/g, '&amp;').replace(/"/g, '&quot;')
}

// ── Static document assets (1:1 with the generated editorial report) ───────────

const STYLES = `
@font-face{font-family:'PP Editorial New';font-style:normal;font-weight:400;font-display:swap;src:url(/fonts/editorial-new/PPEditorialNew-Regular.woff2) format('woff2');}
@font-face{font-family:'PP Editorial New';font-style:italic;font-weight:400;font-display:swap;src:url(/fonts/editorial-new/PPEditorialNew-Italic.woff2) format('woff2');}
@font-face{font-family:'PP Editorial New';font-style:normal;font-weight:700;font-display:swap;src:url(/fonts/editorial-new/PPEditorialNew-Bold.woff2) format('woff2');}
@font-face{font-family:'PP Neue Montreal';font-style:normal;font-weight:400;font-display:swap;src:url(/fonts/neue-montreal-arabic/PPNeueMontrealArabic-Regular.woff2) format('woff2');}
@font-face{font-family:'PP Neue Montreal';font-style:normal;font-weight:500;font-display:swap;src:url(/fonts/neue-montreal-arabic/PPNeueMontrealArabic-Medium.woff2) format('woff2');}
@font-face{font-family:'PP Neue Montreal';font-style:normal;font-weight:700;font-display:swap;src:url(/fonts/neue-montreal-arabic/PPNeueMontrealArabic-Bold.woff2) format('woff2');}

:root{
  --bg:#ffffff; --text:#18181b; --text-2:#52525b; --text-muted:#a1a1aa;
  --border:#e7e5e4; --border-strong:#d6d3d1;
  --accent:#E8590C; --accent-soft:rgba(232,89,12,0.08);
  --chart-ink:#44403c; --chart-grid:#f5f5f4;
  --radius:12px; --maxw:860px;
  --font-display:"PP Editorial New",Georgia,"Times New Roman",serif;
  --font-body:"PP Neue Montreal",system-ui,-apple-system,"Segoe UI",sans-serif;
}
*{box-sizing:border-box;}
html{-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;scroll-behavior:smooth;}
body{margin:0;background:var(--bg);color:var(--text-2);font-family:var(--font-body);font-size:15px;line-height:1.65;}
.page{max-width:var(--maxw);margin:0 auto;padding:44px 32px 96px;}

.masthead h1{font-family:var(--font-display);font-weight:400;font-size:clamp(2rem,4.4vw,2.85rem);line-height:1.07;letter-spacing:-0.01em;color:var(--text);margin:0;}
.masthead .subtitle{font-size:1rem;color:var(--text-2);margin:13px 0 0;max-width:62ch;}
.masthead .rule{width:56px;height:3px;border-radius:2px;background:var(--accent);margin:22px 0 12px;}
.masthead .meta{font-size:0.8rem;color:var(--text-muted);letter-spacing:0.01em;}
.masthead .meta strong{color:var(--text-2);font-weight:500;}

.stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:14px;margin-top:28px;}
.stat{border:1px solid var(--border);border-radius:var(--radius);padding:18px 20px;}
.stat .label{font-size:0.68rem;font-weight:500;letter-spacing:0.09em;text-transform:uppercase;color:var(--text-muted);}
.stat .value{font-family:var(--font-display);font-weight:400;font-size:2.2rem;line-height:1.1;letter-spacing:-0.01em;color:var(--text);margin:8px 0 2px;}
.stat .note{font-size:0.8rem;color:var(--text-muted);}

section{margin-top:60px;scroll-margin-top:76px;}
.section-head{display:flex;align-items:center;gap:14px;margin-bottom:24px;}
.badge{flex:none;width:28px;height:28px;border-radius:50%;background:var(--accent);color:#fff;font-family:var(--font-body);font-weight:500;font-size:0.85rem;display:grid;place-items:center;}
.section-head h2{font-family:var(--font-display);font-weight:400;font-size:1.8rem;letter-spacing:-0.005em;color:var(--text);margin:0;}
.lede{max-width:68ch;margin:0 0 24px;}

.grid-2{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:14px;margin:0 0 8px;}
.grid-3{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:14px;margin:0 0 8px;}
.card{border:1px solid var(--border);border-radius:var(--radius);padding:20px 22px;transition:border-color .15s ease,box-shadow .15s ease,transform .15s ease;}
.card:hover{border-color:var(--border-strong);box-shadow:0 8px 24px rgba(24,24,27,0.06);transform:translateY(-2px);}
.card h3{font-family:var(--font-body);font-weight:500;font-size:1rem;color:var(--text);margin:0 0 8px;display:flex;align-items:center;gap:9px;}
.card h3 .dot{width:7px;height:7px;border-radius:50%;background:var(--accent);flex:none;}
.card p{margin:0;font-size:0.9rem;color:var(--text-2);}

.table-wrap{overflow-x:auto;margin:0 0 12px;}
table{width:100%;border-collapse:collapse;font-size:0.88rem;}
thead th{font-family:var(--font-body);font-size:0.68rem;font-weight:500;letter-spacing:0.07em;text-transform:uppercase;color:var(--text-muted);text-align:left;padding:0 14px 12px;border-bottom:1px solid var(--border-strong);white-space:nowrap;}
tbody td{padding:13px 14px;border-bottom:1px solid var(--border);color:var(--text-2);vertical-align:top;}
tbody td:first-child{font-weight:500;color:var(--text);}
tr.highlight td{background:var(--accent-soft);}
tr.highlight td:first-child{box-shadow:inset 3px 0 0 var(--accent);color:var(--accent);}
.pill{display:inline-block;font-size:0.72rem;font-weight:400;padding:2px 9px;border-radius:999px;border:1px solid var(--border-strong);color:var(--text-2);white-space:nowrap;}
.pill.pill-accent{border-color:var(--accent);color:var(--accent);background:var(--accent-soft);}
.pill.up{border-color:#16a34a;color:#16a34a;}
.pill.down{border-color:#dc2626;color:#dc2626;}
.pill.neutral{border-color:var(--border-strong);color:var(--text-muted);}

.chart-block{margin-top:8px;margin-bottom:24px;}
.chart-title{font-family:var(--font-body);font-weight:500;font-size:0.95rem;color:var(--text);margin-bottom:4px;}
.chart-sub{font-size:0.8rem;color:var(--text-muted);margin-bottom:14px;}
.charts-row{display:flex;flex-direction:column;gap:48px;}
.chart-block--narrow{max-width:460px;}
svg{display:block;width:100%;height:auto;overflow:visible;}
.legend{display:flex;gap:18px;flex-wrap:wrap;margin-top:12px;font-size:0.78rem;color:var(--text-2);}
.legend span{display:inline-flex;align-items:center;gap:7px;cursor:pointer;user-select:none;transition:opacity .15s ease;}
.legend span.off{opacity:0.35;}
.legend i{width:11px;height:11px;border-radius:3px;display:inline-block;}
svg circle[data-tip],svg [data-tip]{cursor:pointer;}

.prose{max-width:68ch;margin:0 0 8px;}
.prose p{margin:0 0 14px;}
.prose p:last-child{margin-bottom:0;}
.prose strong{color:var(--text);font-weight:500;}
.prose em{font-style:italic;}

.conclusion{margin-top:64px;border:1px solid var(--border);border-left:3px solid var(--accent);border-radius:var(--radius);background:var(--accent-soft);padding:28px 30px;}
.conclusion h2{font-family:var(--font-display);font-weight:400;font-size:1.55rem;letter-spacing:-0.005em;color:var(--text);margin:0 0 14px;}
.conclusion p{margin:0 0 10px;max-width:70ch;}
.conclusion p:last-child{margin-bottom:0;}
.conclusion strong{color:var(--text);font-weight:500;}

.confidential{margin-top:48px;padding:20px 24px;border:1px solid var(--border);border-radius:var(--radius);font-size:0.75rem;color:var(--text-muted);line-height:1.6;}
.confidential p{margin:0 0 8px;}
.confidential p:last-child{margin-bottom:0;}

.topnav{position:sticky;top:0;z-index:40;background:rgba(255,255,255,0.85);backdrop-filter:saturate(180%) blur(12px);border-bottom:1px solid var(--border);}
.topnav-inner{max-width:var(--maxw);margin:0 auto;padding:13px 32px;display:flex;align-items:center;gap:20px;flex-wrap:wrap;}
.topnav .nav-brand{font-family:var(--font-display);font-size:1rem;color:var(--text);text-decoration:none;margin-right:auto;}
.topnav a:not(.nav-brand){font-size:0.71rem;font-weight:500;letter-spacing:0.05em;text-transform:uppercase;color:var(--text-muted);text-decoration:none;transition:color .15s ease;white-space:nowrap;}
.topnav a:not(.nav-brand):hover{color:var(--text-2);}
.topnav a.active{color:var(--accent);}

thead th[data-sortable]{cursor:pointer;user-select:none;transition:color .15s ease;}
thead th[data-sortable]:hover{color:var(--text-2);}
thead th .caret{margin-left:5px;font-size:0.9em;opacity:0.35;}
thead th.sorted .caret{opacity:1;color:var(--accent);}

.tip{position:fixed;z-index:100;pointer-events:none;max-width:240px;background:var(--text);color:#fff;font-size:0.74rem;line-height:1.45;padding:8px 11px;border-radius:8px;box-shadow:0 8px 24px rgba(0,0,0,0.20);opacity:0;transform:translateY(4px);transition:opacity .12s ease,transform .12s ease;}
.tip.show{opacity:1;transform:translateY(0);}
.tip b{font-weight:600;}

.js .reveal{opacity:0;transform:translateY(16px);transition:opacity .55s ease,transform .55s ease;}
.js .reveal.in{opacity:1;transform:none;}
@media (prefers-reduced-motion:reduce){.js .reveal{opacity:1;transform:none;transition:none;}}
`

const SCRIPT = `
(function(){
  document.documentElement.classList.add("js");

  // The report renders inside an <iframe>; once focus is in here the host
  // dialog's Escape handler stops firing. Forward Escape to the parent so it
  // can close the dialog (see competitor-sample-artifact.tsx).
  document.addEventListener("keydown",function(e){
    if(e.key==="Escape"){try{parent.postMessage({source:"soku-report",type:"escape"},"*");}catch(_){}}
  });

  // Forward Escape to the host page: once focus is inside this iframe, the
  // parent dialog's document-level Escape handler can't see the keypress, so
  // the dialog would otherwise refuse to close while the report is focused.
  document.addEventListener("keydown",function(e){
    if(e.key==="Escape"||e.keyCode===27){
      try{window.parent.postMessage({source:"soku-report",type:"escape"},"*");}catch(_){}
    }
  });

  var tip=document.createElement("div");tip.className="tip";document.body.appendChild(tip);
  function moveTip(e){var x=Math.min(e.clientX+14,window.innerWidth-tip.offsetWidth-12);tip.style.left=x+"px";tip.style.top=(e.clientY+16)+"px";}
  document.querySelectorAll("[data-tip]").forEach(function(el){
    el.addEventListener("mouseenter",function(e){tip.innerHTML=el.getAttribute("data-tip");tip.classList.add("show");moveTip(e);});
    el.addEventListener("mousemove",moveTip);
    el.addEventListener("mouseleave",function(){tip.classList.remove("show");});
  });

  var links=[].slice.call(document.querySelectorAll('.topnav a[href^="#"]'));
  var byId={};
  links.forEach(function(a){var s=document.getElementById(a.getAttribute("href").slice(1));if(s)byId[s.id]=a;});
  if("IntersectionObserver" in window){
    var spy=new IntersectionObserver(function(entries){
      entries.forEach(function(en){
        if(en.isIntersecting){links.forEach(function(l){l.classList.remove("active");});if(byId[en.target.id])byId[en.target.id].classList.add("active");}
      });
    },{rootMargin:"-45% 0px -50% 0px",threshold:0});
    Object.keys(byId).forEach(function(id){spy.observe(document.getElementById(id));});

    var rev=new IntersectionObserver(function(entries){
      entries.forEach(function(en){if(en.isIntersecting){en.target.classList.add("in");rev.unobserve(en.target);}});
    },{rootMargin:"0px 0px -8% 0px",threshold:0.05});
    document.querySelectorAll(".reveal").forEach(function(el){rev.observe(el);});
  }else{
    document.querySelectorAll(".reveal").forEach(function(el){el.classList.add("in");});
  }

  document.querySelectorAll(".legend span[data-series]").forEach(function(sp){
    sp.addEventListener("click",function(){
      var poly=document.getElementById(sp.getAttribute("data-series"));if(!poly)return;
      var off=sp.classList.toggle("off");poly.style.display=off?"none":"";
    });
  });

  function parseVal(s){
    s=s.trim();
    var m=s.replace(/[, ]/g,"").match(/^[~$]*(-?\\d+(?:\\.\\d+)?)([BKM%]?)/i);
    if(m){var n=parseFloat(m[1]),u=m[2].toUpperCase();if(u==="B")n*=1e9;else if(u==="M")n*=1e6;else if(u==="K")n*=1e3;return {num:n};}
    return {str:s.toLowerCase()};
  }
  document.querySelectorAll("table[data-sortable]").forEach(function(tbl){
    var ths=tbl.tHead.rows[0].cells;
    [].forEach.call(ths,function(th,idx){
      var caret=document.createElement("span");caret.className="caret";caret.textContent="\\u21C5";th.appendChild(caret);
      var asc=true;
      th.addEventListener("click",function(){
        var tb=tbl.tBodies[0];var rows=[].slice.call(tb.rows);
        rows.sort(function(r1,r2){
          var a=parseVal(r1.cells[idx].textContent),b=parseVal(r2.cells[idx].textContent);
          if("num" in a && "num" in b)return asc?a.num-b.num:b.num-a.num;
          var sa="str" in a?a.str:String(a.num),sb="str" in b?b.str:String(b.num);
          return asc?sa.localeCompare(sb):sb.localeCompare(sa);
        });
        rows.forEach(function(r){tb.appendChild(r);});
        [].forEach.call(ths,function(o){o.classList.remove("sorted");var c=o.querySelector(".caret");if(c)c.textContent="\\u21C5";});
        th.classList.add("sorted");caret.textContent=asc?"\\u2191":"\\u2193";asc=!asc;
      });
    });
  });
})();
`
